import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Tooltip } from '@ui/components/Tooltip'
import { AlertIcon } from '@ui/icons/Alert.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { ResponseError, instanceOfAxiosError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useDebouncedValue } from '@utils/debounce'

import { ValidationOperationId, ValidationRuleId } from '@model/ValidationDto'

import { Box } from '@shared/components/Box'

import styles from './PasswordValidation.module.scss'

interface PasswordValidationReturn {
  validationComponent: JSX.Element | null
  isPasswordValid: boolean
}

interface PasswordValidationProps {
  password: string
  confirmPassword?: string
  showOneValidation?: boolean
  validationOperationId?: ValidationOperationId
  passwordResetData?: {
    email: string
    token: string
  }
  leadData?: {
    id: string
    token: string
  }
}

const usePasswordValidationRulesFetch = (props: PasswordValidationProps) => {
  const {
    validationOperationId = ValidationOperationId.ValidateClientPlainPassword,
    leadData,
    passwordResetData,
  } = props

  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const navigate = useNavigate()

  const { data: passwordValidationRules } = useQuery({
    queryKey: queryKeys.config.passwordValidation(validationOperationId, locale),
    queryFn: async () => {
      try {
        if (leadData) {
          return await apiClient.getLeadPasswordValidationRules(leadData, locale)
        }
        if (passwordResetData) {
          return await apiClient.getResetPasswordValidationRules({
            email: passwordResetData.email,
            token: passwordResetData.token,
            languageId: locale,
          })
        }
        return await apiClient.getPasswordValidationRules(validationOperationId, locale)
      } catch (error: unknown) {
        if (instanceOfAxiosError((error as ResponseError)?.response)) {
          const errorResponse = (error as ResponseError).response.response?.data
          if (
            'verifiable_action_unavailable' === errorResponse?.code ||
            errorResponse?.code === 'invalid_token'
          ) {
            navigate('/login')
          }
        }
        return undefined
      }
    },
  })

  return { passwordValidationRules }
}

export const usePasswordValidation = (props: PasswordValidationProps): PasswordValidationReturn => {
  const { password, confirmPassword, showOneValidation } = props

  const apiClient = useApiClient(ClientApiClient)
  const [isSecure, setIsSecure] = useState(true)
  const [isBreachCheckCpmpleted, setIsBreachCheckCpmpleted] = useState(false)

  const { t } = useTranslation()

  const { passwordValidationRules } = usePasswordValidationRulesFetch(props)
  const passwordValidations: Validation[] = []

  if (passwordValidationRules?.validationRules.length) {
    const filteredValidationRules = passwordValidationRules.validationRules.filter(
      (vRule) => vRule.shouldValidateOnFrontEnd
    )

    const newPasswordValidations = filteredValidationRules.map((rule) => {
      const flags = rule.id === ValidationRuleId.StringSplitElementCheck ? 'i' : ''
      const regex = new RegExp(rule.regex, flags)
      return { isValid: regex.test(password), message: rule.ruleText }
    })

    passwordValidations.push(...newPasswordValidations)
  }

  if (props?.passwordResetData || props?.leadData) {
    passwordValidations.push({
      isValid: password === confirmPassword,
      message: t('Validation.Please make sure your password match'),
    })
  }

  const isPasswordValid = passwordValidations.every((pV) => pV.isValid)

  const debouncedValue = useDebouncedValue(password, 500)

  useEffect(() => {
    if (!isPasswordValid || !debouncedValue) return

    const controller = new AbortController()

    async function checkBreach() {
      const isPasswordSecure = await apiClient.checkSecurityBreach(debouncedValue, {
        signal: controller.signal,
      })
      setIsSecure(isPasswordSecure)
      setIsBreachCheckCpmpleted(true)
    }

    checkBreach()

    return () => controller.abort()
  }, [debouncedValue, isPasswordValid, apiClient])

  passwordValidations.push({
    isValid: isSecure,
    message: !isSecure
      ? t('Security breach screening failed; enter new password')
      : t('Must pass security breach screening'),
    tooltip: t(
      'We proactively check your password against known breaches to keep your account safe'
    ),
  })

  const validationComponent = showOneValidation ? (
    passwordValidations.filter((pV) => !pV.isValid)[0] ? (
      <ul>
        <ValidationLine validation={passwordValidations.filter((pV) => !pV.isValid)[0]} />
      </ul>
    ) : null
  ) : (
    <ul>
      {passwordValidations.map((validation, i) => (
        <ValidationLine validation={validation} key={i} />
      ))}
    </ul>
  )

  return {
    validationComponent,
    isPasswordValid: isPasswordValid && isSecure && isBreachCheckCpmpleted,
  }
}

interface Validation {
  isValid: boolean
  message: string
  tooltip?: string
}

export const ValidationLine = ({ validation }: { validation: Validation }) => {
  return (
    <li className={styles.validationPoint}>
      <Box flex align='center'>
        <Box flex mr='x8'>
          <ValidationIcon isValid={validation.isValid} />
        </Box>
        <Body2 as='span' color='text-med-em'>
          {validation.message}
        </Body2>
        {validation.tooltip && (
          <Tooltip content={validation.tooltip}>
            <Box ml='x4'>
              <ActionIcon icon={InfoIcon} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </li>
  )
}

const ValidationIcon: React.FC<{ isValid: boolean }> = ({ isValid }) => {
  return isValid ? (
    <CircleCheckIcon color='text-success-high-em' size={24} />
  ) : (
    <AlertIcon color='text-danger-high-em' size={24} />
  )
}
